import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ChatBubbleLeftRightIcon,
  InboxIcon,
  ChevronRightIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline';

const POLL_INTERVAL_MS = 30000;
const FETCH_LIMIT = 200;

const MobileMessageMenu = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);

  const totalUnread = chatUnread + inboxUnread;

  const formatUnreadLabel = (count) => {
    if (count <= 0) return '';
    const displayCount = count > 999 ? '999+' : count;
    return `${displayCount} New`;
  };

  const fetchUnreadCounts = useCallback(async () => {
    if (!user?.token) {
      setChatUnread(0);
      setInboxUnread(0);
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const [chatResponse, inboxResponse] = await Promise.all([
        axios.get(`/api/messages?messageType=team,channel&limit=${FETCH_LIMIT}`, { headers }),
        axios.get(`/api/messages?messageType=inbox&limit=${FETCH_LIMIT}`, { headers })
      ]);

      const chatMessages = Array.isArray(chatResponse.data) ? chatResponse.data : [];
      const inboxMessages = Array.isArray(inboxResponse.data) ? inboxResponse.data : [];

      setChatUnread(chatMessages.filter(message => !message.read).length);
      setInboxUnread(inboxMessages.filter(message => !message.read).length);
    } catch (error) {
      console.error('Error fetching mobile message unread counts:', error);
      setChatUnread(0);
      setInboxUnread(0);
    }
  }, [user?.token]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [fetchUnreadCounts]);

  useEffect(() => {
    if (!user?.token) return undefined;

    const interval = setInterval(() => {
      fetchUnreadCounts();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchUnreadCounts, user?.token]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCounts();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchUnreadCounts]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleNavigate = (tab) => {
    setIsOpen(false);
    navigate(`/chat?tab=${tab}`);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className="relative p-2 text-gray-700 hover:text-gray-900"
        aria-label="Open messages"
        aria-expanded={isOpen}
      >
        <ChatBubbleLeftRightIcon className="h-5 w-5" />
        {totalUnread > 0 && (
          <span className="absolute top-0.5 right-0.5 block h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/35" />
          <div
            ref={menuRef}
            className="fixed left-0 right-0 z-50 bg-white rounded-b-3xl shadow-xl"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
          >
            <div className="px-6 py-3">
              <button
                type="button"
                onClick={() => handleNavigate('team')}
                className="w-full flex items-center justify-between py-4"
              >
                <div className="flex items-center space-x-4">
                  <ChatBubbleLeftRightIcon className="h-7 w-7 text-gray-900" />
                  <span className="text-2xl font-medium text-gray-900">Chat</span>
                </div>
                <div className="flex items-center space-x-4">
                  {chatUnread > 0 && (
                    <span className="rounded-full bg-emerald-500 px-3 py-1 text-lg font-semibold text-white">
                      {formatUnreadLabel(chatUnread)}
                    </span>
                  )}
                  <ChevronRightIcon className="h-7 w-7 text-gray-900" />
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNavigate('inbox')}
                className="w-full flex items-center justify-between py-4"
              >
                <div className="flex items-center space-x-4">
                  <InboxIcon className="h-7 w-7 text-gray-900" />
                  <span className="text-2xl font-medium text-gray-900">Inbox</span>
                </div>
                <div className="flex items-center space-x-4">
                  {inboxUnread > 0 && (
                    <span className="rounded-full bg-emerald-500 px-3 py-1 text-lg font-semibold text-white">
                      {formatUnreadLabel(inboxUnread)}
                    </span>
                  )}
                  <ChevronRightIcon className="h-7 w-7 text-gray-900" />
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNavigate('announcements')}
                className="w-full flex items-center justify-between py-4"
              >
                <div className="flex items-center space-x-4">
                  <MegaphoneIcon className="h-7 w-7 text-gray-900" />
                  <span className="text-2xl font-medium text-gray-900">Org News</span>
                </div>
                <div className="flex items-center space-x-4">
                  <ChevronRightIcon className="h-7 w-7 text-gray-900" />
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MobileMessageMenu;
